import { Event as MyonEvent, EventHandler, EventListener, KeyCodes } from "silver";
import { ContextualPopup, ContextualPopupBehavior } from "shared/Components/ContextualPopup";
import { generateId } from "shared/functions";

export interface ContextMenuItemEvent extends MyonEvent {
	sender: ContextMenuItem;
}

export class ContextMenuItem {

	public readonly onBeforeActivate = EventListener.create<ContextMenuItemEvent>("beforeactivate");
	public readonly onActivate = EventListener.create<ContextMenuItemEvent>("activate");
	public readonly onTarget = EventListener.create<ContextMenuItemEvent>("target");
	public readonly onDisableStateChange = EventListener.create<ContextMenuItemEvent>("disableStateChange");

	public readonly element: HTMLElement = document.createElement("div");
	private textContainer: HTMLSpanElement = document.createElement("span");
	private iconContainer: HTMLSpanElement = document.createElement("span");

	constructor() {
		this.element.setAttribute("role", "menuitem");
		this.element.id = generateId("cmenuitem-");
		this.element.tabIndex = -1;

		this.element.classList.add("contextMenu_item");
		this.textContainer.classList.add("contextMenu_itemText");
		this.iconContainer.classList.add("contextMenu_itemIcon");

		this.element.append(this.iconContainer, this.textContainer);

		this.element.addEventListener("click", () => this.activate());
		this.element.addEventListener("mouseover", () => this.targeted = true);
		this.element.addEventListener("mouseout", () => this.targeted = false);
	}

	public setIcon(icon: HTMLElement|null): void {
		this.iconContainer.innerHTML = "";
		if (icon != null) {
			this.iconContainer.appendChild(icon);
		}
	}

	public activate(): void {
		this.onBeforeActivate.trigger({ sender: this });
		this.onActivate.trigger({ sender: this });
	}

	get targeted(): boolean {
		return this.element.classList.contains("-targeted");
	}

	set targeted(targeted: boolean) {
		if (this.targeted === targeted) {
			return;
		}
		this.element.classList.toggle("-targeted", targeted);
		if (targeted) {
			this.onTarget.trigger({ sender: this });
		}
	}

	get id(): string {
		return this.element.id;
	}

	get label(): string {
		return this.textContainer.textContent || "";
	}

	set label(labelValue: string) {
		this.textContainer.textContent = labelValue;
	}

	public static make(options: { label: string, icon?: HTMLElement, iconClass?: string, onActivate?: EventHandler<ContextMenuItemEvent> }): ContextMenuItem {
		const item = new ContextMenuItem();
		item.label = options.label;
		if (options.icon) {
			item.setIcon(options.icon);
		} else if (options.iconClass) {
			const icon = document.createElement("i");
			icon.className = options.iconClass;
			item.setIcon(icon);
		}
		if (options.onActivate) {
			item.onActivate(options.onActivate);
		}
		return item;
	}
}

export class ContextMenu extends ContextualPopup {

	private items: HTMLDivElement = document.createElement("div");
	private itemList: ContextMenuItem[] = [];
	private targetItemIndex: number = -1;
	private onTarget: EventHandler<ContextMenuItemEvent> = ({ sender }) => this.targetItem(sender);
	private onBeforeActivate: EventHandler<ContextMenuItemEvent> = () => this.hide();

	constructor(trigger: HTMLElement) {
		super(document.createElement("div"));

		const root = this.content as HTMLElement;

		trigger.setAttribute("aria-haspopup", "true");
		this.setContext(trigger, ContextualPopupBehavior.CLICK);

		root.classList.add("contextMenu");

		this.items.classList.add("contextMenu_items");
		this.items.setAttribute("role", "menu");
		this.items.setAttribute("tabindex", "0");

		root.append(this.items);

		this.items.addEventListener("keydown", (e): void => {
			if (e.keyCode === KeyCodes.UP_ARROW || e.keyCode === KeyCodes.LEFT_ARROW) {
				this.incrementTargetIndex(-1);
			} else if (e.keyCode === KeyCodes.DOWN_ARROW || e.keyCode === KeyCodes.RIGHT_ARROW) {
				this.incrementTargetIndex(1);
			} else if (e.keyCode === KeyCodes.ESC) {
				this.hide();
			} else if (e.keyCode === KeyCodes.ENTER) {
				this.activateTargetedItem();
			} else {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
		});
		this.updateChildNodeAvailability();
	}

	private activateTargetedItem(): void {
		if (!this.itemList.hasOwnProperty(this.targetItemIndex)) {
			return;
		}
		this.itemList[ this.targetItemIndex ].activate();
	}

	private incrementTargetIndex(d: number): void {
		const itemCount = this.itemList.length;
		if (itemCount === 0) {
			throw new Error("No items to increment");
		}
		this.targetItemAtIndex((this.targetItemIndex + d + itemCount) % itemCount);
	}

	private targetItemAtIndex(itemIndex: number): void {
		this.targetItem(this.itemList[itemIndex]);
	}

	private targetItem(item: ContextMenuItem): void {
		const index = this.itemList.indexOf(item);
		if (index === -1) {
			throw new Error("Item does not belong to this context menu");
		}
		this.items.setAttribute("aria-activedescendant", item.id);
		this.itemList.forEach(i => i.targeted = (i === item));
		this.targetItemIndex = index;
	}

	public empty(): void {
		this.itemList.forEach(i => {
			i.onTarget.unsubscribe(this.onTarget);
			i.onBeforeActivate.unsubscribe(this.onBeforeActivate);
		});
		this.itemList = [];
		this.items.innerHTML = "";
		this.items.removeAttribute("aria-activedescendant");
		this.targetItemIndex = -1;
	}

	public addItem(item: ContextMenuItem): void {
		this.items.appendChild(item.element);
		item.onTarget(this.onTarget);
		item.onBeforeActivate(this.onBeforeActivate);
		this.itemList.push(item);
	}

	public show(): void {
		super.show();
		this.items.focus();
		if (!this.itemList.hasOwnProperty(this.targetItemIndex) && this.itemList.length > 0) {
			this.targetItemIndex = 0;
		}
		if (this.itemList.hasOwnProperty(this.targetItemIndex)) {
			this.targetItemAtIndex(this.targetItemIndex);
		}
	}

	public get length(): number {
		return this.itemList.length;
	}
}
